// PWA Utility functions

/**
 * Registers the service worker for PWA functionality
 */
export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then(registration => {
          console.log('Service Worker registered with scope:', registration.scope);
          
          // Handle service worker updates
          registration.onupdatefound = () => {
            const installingWorker = registration.installing;
            if (installingWorker == null) {
              return;
            }
            
            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // At this point, the updated precached content has been fetched,
                  // but the previous service worker will still serve the older
                  // content until all client tabs are closed.
                  console.log('New content is available and will be used when all tabs for this page are closed.');
                  
                  // Dispatch an event to notify the app about the update
                  window.dispatchEvent(new CustomEvent('serviceWorkerUpdateAvailable'));
                } else {
                  // At this point, everything has been precached.
                  console.log('Content is cached for offline use.');
                }
              }
            };
          };
        })
        .catch(error => {
          console.error('Error during service worker registration:', error);
        });
    });
  }
};

/**
 * Checks if the app is running in standalone mode (installed as PWA)
 * @returns {boolean} True if the app is running in standalone mode
 */
export const isRunningAsPWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone || 
         document.referrer.includes('android-app://');
};

/**
 * Checks if the app is running on iOS
 * @returns {boolean} True if the app is running on iOS
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

/**
 * Checks if the app is running on Android
 * @returns {boolean} True if the app is running on Android
 */
export const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};

export const isSchoolRoute = (pathname = window.location.pathname) => {
  return pathname === '/school' || pathname === '/schools' || pathname.startsWith('/school/');
};

export const setPWAInstallContext = (context) => {
  const startUrl = context === 'school' ? '/schools' : '/';
  localStorage.setItem('pwa-install-context', context);

  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) {
    manifestLink.href = context === 'school' ? '/manifest-schools.json' : '/manifest.json';
  }

  return startUrl;
};

export const getPWAInstallContext = () => {
  return localStorage.getItem('pwa-install-context') === 'school' ? 'school' : 'main';
};

/**
 * Checks if the browser supports notifications
 * @returns {boolean} True if the browser supports notifications
 */
export const supportsNotifications = () => {
  return 'Notification' in window;
};

/**
 * Requests notification permission
 * @returns {Promise<string>} A promise that resolves to the permission state
 */
export const requestNotificationPermission = async () => {
  // Only request permission if the app is running as a PWA
  if (!isRunningAsPWA()) {
    console.log('Not requesting notification permission in browser mode');
    return 'denied';
  }
  
  if (!supportsNotifications()) {
    console.log('Notifications not supported');
    return 'denied';
  }
  
  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

// ---- Push subscription helpers ----

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function fetchPublicKey() {
  try {
    const res = await fetch('/user/push/public-key');
    const data = await res.json();
    return data?.publicKey || '';
  } catch (_) {
    return '';
  }
}

export async function subscribeUserToPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    // already subscribed?
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      try {
        const token = localStorage.getItem('token') || '';
        await fetch('/user/push/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-access-token': token },
          body: JSON.stringify({ subscription: existing.toJSON() })
        });
      } catch(_) {}
      return true;
    }
    const pk = await fetchPublicKey();
    if (!pk) return false;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(pk)
    });
    const token = localStorage.getItem('token') || '';
    await fetch('/user/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': token },
      body: JSON.stringify({ subscription: sub.toJSON() })
    });
    return true;
  } catch (e) {
    console.error('subscribeUserToPush failed', e);
    return false;
  }
}

export async function hasClientPushSubscription() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch (_) {
    return false;
  }
}

export async function ensurePushSubscription() {
  try {
    if (Notification.permission !== 'granted') return false;
    const token = localStorage.getItem('token');
    if (!token) return false; // only bind to logged in users
    return await subscribeUserToPush();
  } catch(_) { return false; }
}

export async function subscribeSchoolActorToPush({ basePath, token, schoolId = '', requestPermission = true } = {}) {
  if (!basePath || !token || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  if (!('Notification' in window)) return { ok: false, reason: 'unsupported' };

  try {
    let permission = Notification.permission;
    if (permission !== 'granted' && requestPermission) permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: 'permission_denied' };

    const registration = await navigator.serviceWorker.getRegistration() || await navigator.serviceWorker.register('/service-worker.js');
    let subscription = await registration.pushManager.getSubscription();
    const headers = { 'Content-Type': 'application/json', 'x-access-token': token };
    if (schoolId) headers['x-school-id'] = schoolId;

    if (!subscription) {
      const keyResponse = await fetch(`${basePath}/push/public-key`, { headers });
      const keyData = await keyResponse.json().catch(() => ({}));
      if (!keyResponse.ok || !keyData.publicKey) return { ok: false, reason: 'push_not_configured' };
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyData.publicKey),
      });
    }

    const response = await fetch(`${basePath}/push/subscribe`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ subscription: subscription.toJSON() }),
    });
    if (!response.ok) return { ok: false, reason: 'registration_failed' };
    return { ok: true };
  } catch (error) {
    console.error('subscribeSchoolActorToPush failed', error);
    return { ok: false, reason: 'registration_failed' };
  }
}

export async function unsubscribeSchoolActorFromPush({ basePath, token, schoolId = '' } = {}) {
  if (!basePath || !token) return { ok: false, reason: 'unsupported' };

  try {
    let endpoint = '';
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      endpoint = subscription?.endpoint || '';
      if (subscription) await subscription.unsubscribe();
    }

    const headers = { 'Content-Type': 'application/json', 'x-access-token': token };
    if (schoolId) headers['x-school-id'] = schoolId;
    const response = await fetch(`${basePath}/push/unsubscribe`, {
      method: 'POST',
      headers,
      body: JSON.stringify(endpoint ? { endpoint } : {}),
    });
    if (!response.ok) return { ok: false, reason: 'unregistration_failed' };
    return { ok: true };
  } catch (error) {
    console.error('unsubscribeSchoolActorFromPush failed', error);
    return { ok: false, reason: 'unregistration_failed' };
  }
}

/**
 * Checks if the app can be installed
 * @returns {Promise<boolean>} A promise that resolves to true if the app can be installed
 */
export const canInstallPWA = async () => {
  // Don't show install prompt if already running as PWA
  if (isRunningAsPWA()) {
    return false;
  }
  
  // Check if the browser supports PWA installation
  return 'BeforeInstallPromptEvent' in window;
};

// Store the deferred prompt for later use
let deferredPrompt = null;

export const hasInstallPrompt = () => Boolean(deferredPrompt);

// Listen for the beforeinstallprompt event
window.addEventListener('beforeinstallprompt', (e) => {
  // Prevent the default browser install prompt
  e.preventDefault();
  
  // Store the event for later use
  deferredPrompt = e;
  
  // Dispatch an event to notify the app that installation is available
  window.dispatchEvent(new CustomEvent('pwaInstallAvailable'));
});

/**
 * Shows the PWA install prompt
 * @returns {Promise<boolean>} A promise that resolves to true if the user accepted the installation
 */
export const showInstallPrompt = async () => {
  if (!deferredPrompt) {
    console.log('No installation prompt available');
    return false;
  }
  
  // Show the install prompt
  deferredPrompt.prompt();
  
  // Wait for the user to respond to the prompt
  const choiceResult = await deferredPrompt.userChoice;
  
  // Clear the deferred prompt
  deferredPrompt = null;
  
  // Return true if the user accepted the installation
  return choiceResult.outcome === 'accepted';
};

// Listen for the appinstalled event
window.addEventListener('appinstalled', () => {
  // Clear the deferredPrompt variable
  deferredPrompt = null;
  
  // Log the installation
  console.log('PWA was installed');
  
  // Dispatch an event to notify the app that installation is complete
  window.dispatchEvent(new CustomEvent('pwaInstalled'));
});
